import { useSyncExternalStore, type FormEvent, type ReactNode } from 'react';
import { Lock } from '../../../icons';
import { Avatar } from '../Avatar';
import { Button } from '../Button';
import { Textarea } from '../Input';
import { InlineNote } from '../InlineNote';
import {
  tShared, getSharedComponentLang, subscribeSharedComponentLang,
} from '../../../lib/shared-i18n';
import './CommentThread.css';

/**
 * CommentThread — 댓글 · 의견 (디자인 정본 E10 · 2026-09-15 첫 코드).
 *
 * 쓰이는 곳: 게시판 댓글 · (다음) 결재 의견 · 업무 코멘트. 지금은 도메인마다 따로 그린다(정본: 18파일).
 *
 * 정본 규약:
 *   ★★ **답글은 한 단계까지만** — 2단 이상은 읽기 어렵다. 서버가 더 깊게 줘도 **한 단계로 펴서** 그린다
 *      (`flattenReplies`). 답글에는 「답글」 행동을 두지 않는다.
 *   ★ 들여쓰기는 **좌측 2px 선**.
 *   ★ 시각은 **24시간 안이면 상대**(「12분 전」), 넘으면 날짜(정본 F18 — 같은 해 `MM-DD HH:mm`, 아니면 `YYYY-MM-DD`).
 *   ★ **내 글에만 수정 · 삭제**가 뜬다 — 이 부품은 권한을 판정하지 않는다. 호출부가 `canEdit` · `canDelete` 를
 *     정해 준다(운영자는 남의 댓글도 지울 수 있다 — 그 판정은 서버 규칙이다).
 *   ★ **삭제 확인은 호출부가 `layerConfirm` 으로** 한다 — 무엇을 지우는지 문장이 도메인마다 다르다.
 *   ★ 행동(답글 · 수정 · 삭제)은 **글자 행동**이다 — 정본 E10 견본과 게시판 시안이 그렇게 그렸다.
 *     (「목록 줄의 행동은 테두리 단추」 규칙은 표 · 목록 줄의 것이고, 댓글 본문 아래 행동은 정본이 따로 정했다.)
 *
 * 삭제된 댓글은 이름 · 행동 없이 「삭제된 댓글입니다」로 자리만 남긴다 — 답글이 매달려 있으면 흐름이 끊기지 않게.
 * `locked` 를 주면 입력칸 대신 그 문장을 보인다(「작성자가 이 글에 댓글 허용을 껐습니다」).
 */

export interface CommentAuthor {
  id: number;
  name: string;
  /** 이름 옆 작은 글자 — 부서 · 직급. */
  sub?: string;
  avatarUrl?: string | null;
}

export interface CommentItem {
  id: string;
  author: CommentAuthor | null;
  body: string;
  /** ISO 시각. */
  createdAt: string;
  deleted?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  replies?: readonly CommentItem[];
}

export interface CommentComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  /** 입력칸 위 제목(「댓글 남기기」). */
  label?: ReactNode;
  placeholder?: string;
  maxLength?: number;
  submitting?: boolean;
  /** 입력칸 아래 왼쪽 한 줄(「멘션 알림은 게시판 알림 설정과 관계없이 갑니다」). */
  hint?: ReactNode;
}

export interface CommentThreadProps {
  comments: readonly CommentItem[];
  /** 상대 시각 기준 — 검사가 고정한다. 기본 지금. */
  now?: Date;
  onReply?: (comment: CommentItem) => void;
  onEdit?: (comment: CommentItem) => void;
  onDelete?: (comment: CommentItem) => void;
  /** 없으면 입력칸을 그리지 않는다. */
  composer?: CommentComposerProps | null;
  /** 댓글을 받지 않을 때의 문장. 주면 입력칸 대신 이것을 그린다. */
  locked?: ReactNode;
  /** 댓글이 하나도 없을 때의 문장. */
  emptyText?: ReactNode;
  className?: string;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const pad = (n: number) => String(n).padStart(2, '0');

/** 정본 F18 — 24시간 안은 상대, 그 뒤는 날짜. 순수 함수(검사가 직접 부른다). */
export function formatCommentTime(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = now.getTime() - d.getTime();
  // 기기 시계가 조금 앞선 경우(음수)도 1분 안이면 「방금」이다.
  if (diff > -MINUTE && diff < MINUTE) return tShared('shared.comment.just_now');
  if (diff >= MINUTE && diff < HOUR) return tShared('shared.comment.minutes_ago', { n: Math.floor(diff / MINUTE) });
  if (diff >= HOUR && diff < DAY) return tShared('shared.comment.hours_ago', { n: Math.floor(diff / HOUR) });
  if (d.getFullYear() === now.getFullYear()) {
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `@이름` 을 따로 떼어 낸다 — 홀수 칸이 멘션이다. */
export function splitMentions(body: string): string[] {
  return body.split(/(@[\p{L}\p{N}_.-]+)/u);
}

/** 답글의 답글을 **한 단계로 편다** — 깊이 우선 차례를 지킨다. */
export function flattenReplies(replies: readonly CommentItem[] | undefined): CommentItem[] {
  const out: CommentItem[] = [];
  const walk = (list: readonly CommentItem[] | undefined) => {
    for (const r of list ?? []) {
      out.push({ ...r, replies: undefined });
      walk(r.replies);
    }
  };
  walk(replies);
  return out;
}

function CommentRow({
  comment, reply, now, onReply, onEdit, onDelete,
}: {
  comment: CommentItem;
  reply: boolean;
  now: Date;
  onReply?: (c: CommentItem) => void;
  onEdit?: (c: CommentItem) => void;
  onDelete?: (c: CommentItem) => void;
}) {
  const deleted = !!comment.deleted;
  const author = deleted ? null : comment.author;
  const showReply = !reply && !deleted && !!onReply;
  const showEdit = !deleted && !!comment.canEdit && !!onEdit;
  const showDelete = !deleted && !!comment.canDelete && !!onDelete;

  return (
    <article
      id={`c-${comment.id}`}
      className={['wc-comment', reply ? 'wc-comment--reply' : '', deleted ? 'wc-comment--deleted' : '']
        .filter(Boolean).join(' ')}
    >
      <div className="wc-comment__avatar">
        {author
          ? <Avatar userId={author.id} name={author.name} src={author.avatarUrl ?? null} size={reply ? 'xs' : 'sm'} />
          : <span className="wc-comment__avatar-blank" aria-hidden />}
      </div>
      <div className="wc-comment__main">
        <div className="wc-comment__head">
          {author && <b className="wc-comment__name">{author.name}</b>}
          {author?.sub && <span className="wc-comment__sub">{author.sub}</span>}
          <time className="wc-comment__time" dateTime={comment.createdAt}>
            {formatCommentTime(comment.createdAt, now)}
          </time>
        </div>
        <p className="wc-comment__body">
          {deleted
            ? tShared('shared.comment.deleted')
            : splitMentions(comment.body).map((part, i) => (i % 2 === 1
              ? <span key={i} className="wc-comment__mention">{part}</span>
              : part))}
        </p>
        {(showReply || showEdit || showDelete) && (
          <div className="wc-comment__acts">
            {showReply && (
              <button type="button" className="wc-comment__act" onClick={() => onReply?.(comment)}>
                {tShared('shared.comment.reply')}
              </button>
            )}
            {showEdit && (
              <button type="button" className="wc-comment__act" onClick={() => onEdit?.(comment)}>
                {tShared('shared.comment.edit')}
              </button>
            )}
            {showDelete && (
              <button type="button" className="wc-comment__act" onClick={() => onDelete?.(comment)}>
                {tShared('shared.comment.delete')}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export function CommentThread({
  comments, now, onReply, onEdit, onDelete, composer, locked, emptyText, className,
}: CommentThreadProps) {
  // 언어가 바뀌면 다시 그린다(다른 공통 부품과 같은 방식).
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang);
  const at = now ?? new Date();

  const canSubmit = !!composer && composer.value.trim().length > 0 && !composer.submitting;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) composer?.onSubmit();
  };

  return (
    <section className={['wc-comments', className ?? ''].filter(Boolean).join(' ')}>
      {comments.length > 0 ? (
        <ol className="wc-comments__list" aria-label={tShared('shared.comment.list_aria')}>
          {comments.map((c) => {
            const replies = flattenReplies(c.replies);
            return (
              <li key={c.id} className="wc-comments__group">
                <CommentRow comment={c} reply={false} now={at} onReply={onReply} onEdit={onEdit} onDelete={onDelete} />
                {replies.length > 0 && (
                  <ol className="wc-comments__replies">
                    {replies.map((r) => (
                      <li key={r.id}>
                        <CommentRow comment={r} reply now={at} onEdit={onEdit} onDelete={onDelete} />
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            );
          })}
        </ol>
      ) : (emptyText != null && <p className="wc-comments__empty">{emptyText}</p>)}

      {locked != null ? (
        <InlineNote tone="info" icon={Lock} className="wc-comments__locked">{locked}</InlineNote>
      ) : composer ? (
        <form className="wc-comments__composer" onSubmit={submit}>
          {composer.label != null && <div className="wc-comments__composer-label">{composer.label}</div>}
          <Textarea
            value={composer.value}
            onChange={(e) => composer.onChange(e.target.value)}
            placeholder={composer.placeholder ?? tShared('shared.comment.placeholder')}
            maxLength={composer.maxLength}
            aria-label={tShared('shared.comment.input_aria')}
            disabled={composer.submitting}
            fullWidth
          />
          <div className="wc-comments__composer-foot">
            <span className="wc-comments__hint">{composer.hint}</span>
            {composer.maxLength != null && (
              <span className="wc-comments__count">
                {composer.value.length.toLocaleString()} / {composer.maxLength.toLocaleString()}
              </span>
            )}
            {composer.onCancel && (
              <Button type="button" variant="secondary" size="compact" onClick={composer.onCancel}>
                {tShared('shared.comment.cancel')}
              </Button>
            )}
            <Button type="submit" variant="primary" size="compact" disabled={!canSubmit} loading={composer.submitting}>
              {tShared('shared.comment.submit')}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
